// schema
import * as ISchema from "#webapp/src/__schema__";

// libraries
import { ApolloError, useMutation } from "@apollo/client";
import * as React from "react";
import toast from "react-hot-toast";

// mutations
import { DeleteAsanaCredentialsMutationDocument } from "./__generated__/DeleteAsanaCredentials.generated";

// components
import { A } from "#webapp/src/components/ui";
import { AsanaLogo } from "@clockwise/web-commons/src/ui/asana-logo";

// // util
import { TrackingEvents, asanaTrack as track } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { paths, url } from "@clockwise/client-commons/src/constants/site";
import { getServerUrl } from "@clockwise/web-commons/src/util/post-message-common.util";

// // material-ui and styles
import { Delete } from "@clockwise/design-system/icons";
import { Button, makeStyles } from "@material-ui/core";
import { noop } from "lodash";

interface AsanaSettingsRelayProps {
  org: ISchema.IOrg;
  refetch: () => void;
  hideDescription?: boolean;
}

const useStyles = makeStyles({
  btnText: {
    paddingTop: 2,
    display: "inline-flex",
    marginTop: 4,
  },
  asanaLogo: { width: 87, marginTop: 2, marginLeft: 4 },
  trashCan: {
    marginTop: 4,
  },
});

export const AsanaSettings = (props: AsanaSettingsRelayProps) => {
  const classes = useStyles();
  const { org } = props;
  const [deleteAsanaCredentials] = useMutation(DeleteAsanaCredentialsMutationDocument, {
    onCompleted: () => {
      track(TrackingEvents.AUTH.ASANA.DELETE_ASANA_CREDENTIALS);
      toast.success("Clockwise removed from Asana");
      props.refetch();
    },
    onError: (e: ApolloError) => {
      logger.error("Unable to delete Asana Credentials.", e);
      toast.error("Failed to removed Clockwise from Asana. Please try again.");
      track(TrackingEvents.AUTH.ASANA.FAILED_DELETE_ASANA_CREDENTIALS);
    },
  });

  if (!org) {
    return <></>;
  }

  const hasAsanaCredentials =
    org.asanaAccountErrorable.__typename === "AsanaAccount" &&
    org.asanaAccountErrorable.hasAsanaCredentials;

  const asanaAuthUrl = getServerUrl() + paths.asana;

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onRemoveAsana = () => {
    try {
      // Send code back to server!
      void deleteAsanaCredentials({
        variables: {
          orgId: org.id,
        },
      });
    } catch (e) {
      logger.error("Failed to execute deleteAsanaCredentials mutation from AsanaSettings", e);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <div>
      {!props.hideDescription && (
        <>
          <div className="cw-heading-xl cw-mb-3">Asana</div>
          <div className="cw-body-base">
            Schedule Clockwise task holds directly in Asana.&nbsp;
            <A target="_blank" href={`${url}${paths.asanaLanding}`}>
              Learn more.
            </A>
          </div>
        </>
      )}
      <Button
        fullWidth
        variant="outlined"
        onClick={hasAsanaCredentials ? onRemoveAsana : noop}
        style={{ marginTop: 16, width: "auto" }}
      >
        {hasAsanaCredentials ? (
          <span style={{ display: "inline-flex" }}>
            <Delete className={classes.trashCan} />
            <span className={classes.btnText}>
              &nbsp;Remove <AsanaLogo className={classes.asanaLogo} />
            </span>
          </span>
        ) : (
          <span className={classes.btnText} onClick={() => window.open(asanaAuthUrl, "_blank")}>
            Add to <AsanaLogo className={classes.asanaLogo} />
          </span>
        )}
      </Button>
    </div>
  );
};
